import { Status as OrderStatus } from "utils/models/order";
import { Status as QuoteStatus } from "utils/models/quote";
import { SyncKinds } from "utils/models/sync";
import { Role } from "utils/models/user";

export enum BannerType {
  FLOATING = "floating",
  STATIC = "static",
}

export type Banner = {
  id: string;
  name: string;
  image_url: string;
  active: boolean;
  banner_type: BannerType;
  link?: string;
};

export type DeliveryCost = {
  id: string;
  cost: number;
  min_amount: number;
};

export type Promotion = {
  id: string;
  minimum_amount: number;
  discount: number;
  image_url: string;
  start_date: string;
  end_date: string;
  brand_id?: string;
  brand_name?: string;
  product_id?: string;
  product_name?: string;
  paused: boolean;
};

export type Brand = {
  id: string;
  name: string;
  active: boolean;
  show_home?: boolean;
  image_url?: string;
};

export type Group = {
  id: string;
  name: string;
  active: boolean;
};

export type Line = {
  id: string;
  name: string;
  group_id: string;
  group_name: string;
  active: boolean;
};

export type Company = {
  id: string;
  name: string;
  cuit: string;
  billing_taxes_category: string;
  discount: number;
  email?: string;
  active: boolean;
  addresses: { id: string; label: string }[];
};

export type CompanyDiscount = {
  active: boolean;
  id: string;
  discount: number;
  company_id: string;
  company_name: string;
  brand_id?: string;
  brand_name?: string;
  product_id?: string;
  product_name?: string;
};

export type User = {
  id: string;
  email: string;
  name: string;
  phone: string;
  role: Role;
  company: Company;
  active?: boolean;
};

export type PresignedUrl = {
  presigned_url: string;
  url: string;
  fields: {
    key: string;
    success_action_status: string;
    acl: string;
    policy: string;
    "x-amz-credential": string;
    "x-amz-algorithm": string;
    "x-amz-date": string;
    "x-amz-signature": string;
  };
};

export type Product = {
  external_id: string;
  id: string;
  name: string;
  description: string;
  oem_codes?: string[];
  equivalents: string[];
  related: string[];
  min_mult: number;
  active: boolean;
  line_id: string;
  line_name: string;
  group_id: string;
  group_name: string;
  brand_id: string;
  brand_name: string;
  pictures?: string[];
  image_url: string;
  tech_sheet_url?: string;
  stock?: number;
  price?: number;
  company_discount?: number;
  promotion_discount?: number;
  promotion_minimum_amount?: number;
  company_margin?: number;
  tax: number;
};

export type Sync = {
  id: string;
  last_synced_at: string;
  last_success_synced_at: string;
  kind: SyncKinds;
  syncing: boolean;
  success: boolean;
  errored: boolean;
};

export type ShoppingCartItem = {
  product: Product;
  amount: number;
};

export type QuoteItem = {
  id: string;
  product: Pick<Product, "active" | "oem_codes" | "tax" | "stock" | "min_mult">;
  amount: number;
  product_id: string;
  product_name: string;
  product_image_url: string;
  product_external_id: string;
  product_description: string;
  line_id: string;
  line_name: string;
  group_id: string;
  group_name: string;
  brand_id: string;
  brand_name: string;
  product_price?: number;
  company_discount_id?: string;
  company_discount?: number;
  promotion_id?: string;
  promotion_discount?: number;
  promotion_minimum_amount?: number;
};

export type Quote = {
  id: string;
  status: QuoteStatus;
  company: Company;
  quote_items: QuoteItem[];
  company_base_discount: number;
  created_at: string;
  creator?: User;
};

export type OrderItem = {
  id: string;
  amount: number;
  product_id: string;
  product_name: string;
  product_oem_codes?: string[];
  product_image_url: string;
  product_price?: number;
  product_description: string;
  product_external_id: string;
  line_id: string;
  line_name: string;
  group_id: string;
  group_name: string;
  brand_id: string;
  brand_name: string;
  company_discount_id?: string;
  company_discount?: number;
  promotion_id?: string;
  promotion_discount?: number;
  promotion_minimum_amount?: number;
  promotion_start_date?: string;
  promotion_end_date?: string;
  product_itris_final_discount: number;
  product_itris_final_unit_price: number;
  DEPRECATED_product_final_unit_price: number;
  product_tax_multiplier: number;
  product_human_tax: string;
  with_saved_discount: boolean;
};

export type Order = {
  id: string;
  quote_id: string;
  created_at: string;
  status: OrderStatus;
  company: Company;
  company_base_discount: number;
  order_items: OrderItem[];
  external_id?: string;
  external_result_message?: string;
  delivery?: boolean;
  address?: string;
  delivery_cost?: number;
};

export type StockNotification = {
  id: string;
  product_id: string;
  user_id: string;
  product: Product;
  user: User;
};
